<script setup lang="ts">
/**
 * A section container used for grouping and organizing content, with an
 * optional header that supports title, badges, actions, and expandability.
 *
 * Example:
 *
 * <RcSection title="Section title" type="secondary" mode="with-header" :expandable="false" background="secondary">
 *   <p>Section content here</p>
 * </RcSection>
 *
 * The default slot stacks whatever is written into it 16px apart, so form
 * elements go straight in and no call site needs a wrapper div for the
 * spacing.
 *
 * <RcSection title="Section title" type="secondary" mode="with-header" background="secondary">
 *   <LabeledInput label="Name" />
 *   <LabeledInput label="Description" />
 * </RcSection>
 *
 * A section that needs several groups writes RcContentGroups into that same
 * slot. The section spaces groups 24px apart, and each group stacks its own
 * content 16px apart.
 *
 * <RcSection title="Section title" type="secondary" mode="with-header" background="secondary">
 *   <RcContentGroup>
 *     <LabeledInput label="Name" />
 *     <LabeledInput label="Description" />
 *   </RcContentGroup>
 *   <RcContentGroup>
 *     <LabeledInput label="Namespace" />
 *   </RcContentGroup>
 * </RcSection>
 *
 * <RcSection title="Section title" type="secondary" mode="with-header" expandable v-model:expanded="expanded" background="secondary">
 *   <template #counter>
 *     <RcCounterBadge :count="99" type="inactive" />
 *   </template>
 *   <template #errors>
 *     <RcIcon v-clean-tooltip="'3 validation errors'" type="error" size="large" status="error" />
 *   </template>
 *   <template #badges>
 *     <RcSectionBadges :badges="[
 *       { label: 'Status', status: 'success', tooltip: 'All systems operational' },
 *       { label: 'Status', status: 'warning', tooltip: 'Degraded performance' },
 *       { label: 'Status', status: 'error', tooltip: 'Service unavailable' },
 *     ]" />
 *   </template>
 *   <template #actions>
 *     <RcSectionActions :actions="[
 *       { label: 'Action', icon: 'chevron-left', action: () => {} },
 *       { icon: 'copy', ariaLabel: 'Copy', action: () => {} },
 *       { icon: 'trash', label: 'Delete', action: () => {} },
 *     ]" />
 *   </template>
 *   <p>Section content here</p>
 * </RcSection>
 *
 * A collapsed section keeps its content mounted but hidden, so form fields inside it keep validating.
 * Content that sizes itself from the DOM on mount measures zero while hidden, and should measure again
 * once shown (see observeReveal in @components/utils/reveal). Content that can't do this can set
 * `unmountOnHide` to be unmounted while collapsed instead.
 *
 * Form fields report whether they show an error to the section they are in (see useSectionValidity). A section with
 * such fields, including ones in nested sections, shows an error icon in its #errors slot, expanded or not.
 * Filling the slot replaces the icon, and the slot's `invalid` prop says whether it would be shown. The section's
 * table of contents entry shows the same icon. Collapsing a section validates it: its fields show their errors
 * from then on, without waiting to be touched.
 */
import {
  computed, inject, nextTick, provide, useTemplateRef, watch, type Ref
} from 'vue';
import RcButton from '@components/RcButton/RcButton.vue';
import RcIcon from '@components/RcIcon/RcIcon.vue';
import { useInSummary } from '@shell/components/TableOfContents/composables';
import type { RcSectionProps, SectionBackground } from './types';
import { provideSectionValidity } from './useSectionValidity';

const RC_SECTION_BG_KEY = 'rc-section-background';

const props = withDefaults(defineProps<RcSectionProps>(), { title: '', unmountOnHide: false });

const parentBackground = inject<Ref<SectionBackground> | null>(RC_SECTION_BG_KEY, null);

const resolvedBackground = computed<SectionBackground>(() => {
  if (props.background) {
    return props.background;
  }

  const parent = parentBackground?.value ?? null;

  return parent === 'primary' ? 'secondary' : 'primary';
});

provide(RC_SECTION_BG_KEY, resolvedBackground);

const expanded = defineModel<boolean>('expanded', { default: true });

const { hasInvalid, validate } = provideSectionValidity();

watch(expanded, (isExpanded) => {
  if (!isExpanded) {
    validate();
  }
});

// Expose summary, name, and a display label on the component public instance so
// TOC discovery can access component
const displayTitle = computed(() => props.title);

const name = 'RcSection';

// Register this section in form summary/table-of-contents context (if provided)
const sectionRef = useTemplateRef<HTMLElement>('rc-section-summarized-container');

// Navigating to a section from the table of contents opens it if it's collapsed, then scrolls to it
function scrollTo() {
  if (props.expandable) {
    expanded.value = true;
  }

  nextTick(() => sectionRef.value?.scrollIntoView(true));
}

const { summary } = useInSummary({
  label:      displayTitle,
  scrollTo,
  elementRef: sectionRef,
  invalid:    hasInvalid,
});

defineExpose({
  summary, displayTitle, name
});

const hasHeader = computed(() => {
  return props.mode === 'with-header';
});

const sectionClass = computed(() => ({
  'rc-section':     true,
  'type-primary':   props.type === 'primary',
  'type-secondary': props.type === 'secondary',
  'bg-primary':     resolvedBackground.value === 'primary',
  'bg-secondary':   resolvedBackground.value === 'secondary',
}));

const contentClass = computed(() => ({
  'section-content':    true,
  'no-header':          !hasHeader.value,
  'expandable-content': props.expandable,
}));

function toggle() {
  if (props.expandable) {
    expanded.value = !expanded.value;
  }
}
</script>

<template>
  <div
    ref="rc-section-summarized-container"
    :class="sectionClass"
  >
    <div
      v-if="hasHeader"
      class="section-header"
      :class="{ expandable: props.expandable, collapsed: !expanded }"
      @click="toggle"
    >
      <div class="left-wrapper">
        <RcButton
          v-if="props.expandable"
          class="toggle-button"
          variant="ghost"
          :aria-expanded="expanded"
          :aria-label="expanded ? 'Collapse section' : 'Expand section'"
          @click.stop="toggle"
        >
          <RcIcon
            :type="expanded ? 'chevron-down' : 'chevron-right'"
            size="medium"
          />
        </RcButton>
        <div class="title">
          <slot name="title">
            {{ props.title }}
          </slot>
          <div
            v-if="$slots.counter"
            class="counter"
          >
            <slot name="counter" />
          </div>
          <slot
            name="errors"
            :invalid="hasInvalid"
          >
            <RcIcon
              v-if="hasInvalid"
              v-clean-tooltip="t('component.rcSection.invalidFields')"
              type="error"
              size="large"
              status="error"
              data-testid="rc-section-invalid-fields"
            />
          </slot>
        </div>
      </div>
      <div
        v-if="$slots.badges || $slots.actions"
        class="right-wrapper"
      >
        <div
          v-if="$slots.badges"
          class="status-badges"
        >
          <slot name="badges" />
        </div>
        <div
          v-if="$slots.actions"
          class="actions"
          @click.stop
        >
          <slot name="actions" />
        </div>
      </div>
    </div>
    <!--
      v-show (not v-if) keeps collapsed content mounted, so form fields inside
      it stay registered with their validation (e.g. vee-validate useField)
      and keep reporting errors while the section is collapsed.
      unmountOnHide opts back into unmounting collapsed content.
    -->
    <div
      v-if="expanded || !props.unmountOnHide"
      v-show="expanded"
      :class="contentClass"
    >
      <slot />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.rc-section {
  display: flex;
  flex-direction: column;
  gap: 8px;

  &.type-primary {
    .title {
      font-weight: 700;
    }
  }

  &.type-secondary {
    padding: 0 16px;
    border-radius: 8px;

    .title {
      font-weight: 600;
    }
  }

  &.bg-primary {
    background-color: var(--rc-section-background-primary);
  }

  &.bg-secondary {
    background-color: var(--rc-section-background-secondary);

    > .section-header .counter {
      --rc-counter-badge-inactive-background: var(--rc-section-counter-background);
      --rc-counter-badge-inactive-border: var(--rc-section-counter-border);
    }
  }
}

.section-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 24px;
  height: 56px;

  &.expandable {
    cursor: pointer;
    user-select: none;
  }
}

.left-wrapper {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;

  .toggle-button + .title {
    margin-left: -4px;
  }
}

.title {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  font-size: 18px;
  line-height: 1.2;
  color: var(--body-text, inherit);
}

.counter {
  display: inline-flex;
  align-items: center;
}

// TODO: Considering removing specificity override when RcButton sizes are refactored (#18062)
.left-wrapper :deep(button.toggle-button.btn-medium:not(.btn-sm)) {
  flex-shrink: 0;
  font-size: 16px;
  color: var(--body-text, inherit);
  padding: 0;
  min-height: initial;
}

.right-wrapper {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  gap: 24px;
  margin-left: auto;
}

.status-badges {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
}

.actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0;
}

.section-content {
  display: flex;
  flex-direction: column;
  gap: var(--gap-md, 16px);
  padding: 0 0 16px;
  color: var(--body-text);

  &:has(> .rc-content-group) {
    gap: var(--gap-lg, 24px);
  }

  &.expandable-content {
    padding: 0 0 16px 24px;
  }

  &.no-header {
    padding: 16px 0;
  }
}
</style>
